import React, { useRef, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { TZDate } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { DateTime } from '@/components/ui/datetime';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { isSameDay } from 'date-fns';
import { coerceToUtcDate, keepOffset } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { TimePicker } from './TimePicker';

/**
 * Controlled open state: a parent passing `open` must take `onOpenChange`,
 * or Cancel and Escape could never close the (modal) popover. Without `open`
 * the picker manages its own open state and only reports changes.
 */
type OpenStateProps =
  | { open: boolean; onOpenChange: (open: boolean) => void }
  | { open?: undefined; onOpenChange?: (open: boolean) => void };

export type DatePickerButtonProps = OpenStateProps & {
  /**
   * The stored filter value ('' when unset): an ISO instant, or any older
   * shape `coerceToUtcDate` reads. Shown and edited in the display time zone.
   */
  value: string;
  /** Called on Apply with the value to stage (see the component docs). */
  onApply: (value: string) => void;
  /**
   * With no value, open on the current minute (Is After) instead of today's
   * 00:00.
   */
  seedWithCurrentTime?: boolean;
  /** Called on every draft edit, so a parent can drop an error it fixed. */
  onDraftChange?: () => void;
  placeholder?: string;
  className?: string;
  /**
   * Range start endpoint to indicate (From). Rendered in its role color
   * independently of value. Same day (display zone) as rangeEnd → To wins.
   */
  rangeStart?: Date;
  /** Range end endpoint to indicate (To). Rendered in its role color. */
  rangeEnd?: Date;
  /** Forwarded to the trigger Button so the parent can focus it later. */
  triggerRef?: React.Ref<HTMLButtonElement>;
  /** On an Apply-driven close, focus moves here instead of back to the trigger. */
  focusOnCloseRef?: React.RefObject<HTMLElement | null>;
  /** Shown (and announced) inside the open popover, e.g. a rejected Apply. */
  error?: string;
};

// Cut to the minute on the timeline, as a plain Date. Not date-fns'
// startOfMinute: its local setters move a time in the browser zone's repeated
// (fall-back) hour to the first occurrence, an hour earlier.
const toMinute = (date: Date): Date =>
  new Date(Math.floor(date.getTime() / 60_000) * 60_000);

/**
 * Date + time picker for date filters. The popover drafts a day and a time
 * (in the user's display time zone, named next to the time). Nothing is
 * emitted until Apply; Cancel, Escape, or an outside click discards the
 * draft. The popover is modal, so that outside click cannot also press a
 * control behind it (like the filter's own Apply).
 *
 * Filter boundaries are picked to the minute. The draft never carries seconds
 * or milliseconds, and an edited value is emitted without a milliseconds
 * field: we don't want any millisecond representation, since that precision is
 * noise and makes the UX worse. Apply without an edit re-emits the stored
 * value verbatim, so an untouched bound (a legacy date-only value, or a deep
 * link's exact instant) keeps the meaning the backend gives it.
 *
 * Picking a day keeps a committed or typed time. The current-minute seed only
 * belongs to today, so picking another day resets it to 00:00.
 */
export const DatePickerButton: React.FC<DatePickerButtonProps> = ({
  value,
  onApply,
  seedWithCurrentTime = false,
  onDraftChange,
  placeholder = 'Select date',
  className,
  rangeStart,
  rangeEnd,
  open,
  onOpenChange,
  triggerRef,
  focusOnCloseRef,
  error,
}) => {
  const timeZone = useDisplayTimeZone();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const popoverOpen = isControlled ? open : internalOpen;
  const setPopoverOpen = (next: boolean): void => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const committed = coerceToUtcDate(value);

  // Seeded on every open, by the user or the parent: the committed value,
  // else the current minute (Is After) or today's 00:00, in the display zone.
  const seedDraft = (): Date => {
    if (committed) return toMinute(committed);
    const now = new TZDate(toMinute(new Date()), timeZone);
    if (!seedWithCurrentTime) now.setHours(0, 0);
    return now;
  };
  const [draft, setDraft] = useState(seedDraft);
  // Any day or time edit since open; Apply without one re-emits `value`.
  const [edited, setEdited] = useState(false);
  // A time the user owns (committed or typed) survives a day change.
  const [keepTime, setKeepTime] = useState(committed !== null);
  const [wasOpen, setWasOpen] = useState(popoverOpen);
  if (popoverOpen !== wasOpen) {
    setWasOpen(popoverOpen);
    if (popoverOpen) {
      setDraft(seedDraft());
      setEdited(false);
      setKeepTime(committed !== null);
    }
  }
  // The draft's day and clock as read in the display zone.
  const zonedDraft = new TZDate(draft, timeZone);
  const appliedRef = useRef(false);

  // Every close that comes through here (Escape, outside click, Cancel)
  // discards the draft, even after an Apply the parent rejected.
  const handleOpenChange = (next: boolean): void => {
    if (!next) appliedRef.current = false;
    setPopoverOpen(next);
  };

  const editDraft = (next: Date): void => {
    setDraft(next);
    setEdited(true);
    onDraftChange?.();
  };

  const handleDaySelect = (day: Date | undefined): void => {
    // react-day-picker emits undefined when the selected day is clicked again.
    if (!day) return;
    const next = new TZDate(day, timeZone);
    next.setHours(
      keepTime ? zonedDraft.getHours() : 0,
      keepTime ? zonedDraft.getMinutes() : 0,
      0,
      0
    );
    // A kept time in the repeated (fall-back) hour stays in the draft's
    // occurrence, as a time edit does.
    editDraft(keepOffset(zonedDraft, next));
  };

  const handleTimeChange = (next: Date): void => {
    setKeepTime(true);
    editDraft(next);
  };

  const handleApply = (): void => {
    appliedRef.current = true;
    // A plain Date: TZDate's toISOString() carries the zone offset, not `Z`.
    // Cut to the minute here, so dropping `.000` never loses precision.
    onApply(
      edited || !committed
        ? toMinute(draft).toISOString().replace('.000Z', 'Z')
        : value
    );
    // Controlled parents close (or keep open on a validation error) themselves.
    if (!isControlled) setPopoverOpen(false);
  };

  // Indicate both range endpoints by role (the calendar matches each to its
  // day in the display zone). When From and To fall on the same day, To
  // (blue) wins so only one cell renders.
  const sameDay =
    rangeStart &&
    rangeEnd &&
    isSameDay(new TZDate(rangeStart, timeZone), new TZDate(rangeEnd, timeZone));
  const calendarModifiers =
    rangeStart || rangeEnd
      ? {
          rangeStart: rangeStart && !sameDay ? [rangeStart] : [],
          rangeEnd: rangeEnd ? [rangeEnd] : [],
        }
      : undefined;

  const handleCloseAutoFocus = (event: Event): void => {
    if (appliedRef.current && focusOnCloseRef?.current) {
      event.preventDefault();
      focusOnCloseRef.current.focus();
    }
    appliedRef.current = false;
  };

  return (
    <Popover modal open={popoverOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-normal',
            !committed && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {committed ? (
            <DateTime value={committed} format="minute" />
          ) : (
            placeholder
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onCloseAutoFocus={handleCloseAutoFocus}
      >
        <CalendarComponent
          mode="single"
          timeZone={timeZone}
          selected={draft}
          defaultMonth={draft}
          onSelect={handleDaySelect}
          autoFocus
          modifiers={calendarModifiers}
          // Lighten today only in range mode, to distinguish it from the To marker.
          classNames={
            calendarModifiers
              ? {
                  today:
                    'bg-accent-hover text-accent-hover-foreground rounded-md data-[selected=true]:rounded-none',
                }
              : undefined
          }
        />
        <div className="border-t p-3">
          <TimePicker
            date={zonedDraft}
            onChange={handleTimeChange}
            timeZone={timeZone}
          />
          {error && (
            <p role="alert" className="mt-2 text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
